import { useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {Truck, Container, Ship, Plane, ArrowRight, ArrowLeft, Calculator as CalcIcon} from 'lucide-react'
import {
  calculateFreight,
  formatUSD,
  MODE_LABELS,
  type CalcInput,
  type TransportMode,
  type CargoType,
} from '@/lib/freight'
import LeadForm from '@/components/LeadForm'

const MODES: { key: TransportMode; icon: typeof Truck; sub: string }[] = [
  { key: 'LTL', icon: Truck, sub: 'Less than load & truck freight shipping' },
  { key: 'FTL', icon: Truck, sub: 'Truckload shipping & trucking rates per mile' },
  { key: 'OCEAN', icon: Container, sub: 'Ocean freight forwarders & container shipping' },
  { key: 'AIR', icon: Plane, sub: 'International air freight & air cargo rates' },
]

const MODE_ICON: Record<TransportMode, typeof Truck> = {
  LTL: Truck,
  FTL: Truck,
  OCEAN: Ship,
  AIR: Plane,
}

const DEFAULTS: CalcInput = {
  mode: 'LTL',
  origin: '',
  destination: '',
  weight: 1000,
  weightUnit: 'lbs',
  length: 48,
  width: 40,
  height: 48,
  dimUnit: 'in',
  cargoType: 'Standard',
  oceanContainer: 'FCL',
}

function Calculator() {
  const [step, setStep] = useState(1)
  const [input, setInput] = useState<CalcInput>(DEFAULTS)
  const [calculated, setCalculated] = useState(false)

  const set = <K extends keyof CalcInput>(key: K, value: CalcInput[K]) => {
    setInput((prev) => ({ ...prev, [key]: value }))
  }

  // Derived live result — recomputes whenever inputs change
  const result = useMemo(() => calculateFreight(input), [input])

  const fieldCls =
    'w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-[#0F172A] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent transition'
  const labelCls = 'block text-sm font-medium text-slate-700 mb-1.5'

  return (
    <section id="calculator" className="relative -mt-12 pb-4">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          {/* Progress header */}
          <div className="flex items-center gap-3 px-6 sm:px-8 py-5 bg-[#F1F5F9] border-b border-slate-200">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#1E293B] text-white">
              <CalcIcon className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-lg font-bold text-[#0F172A]">Interactive Freight Calculator</h2>
              <p className="text-xs text-slate-500">
                Step {step} of 2 — {step === 1 ? 'Select transport mode' : 'Enter cargo specifications'}
              </p>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <AnimatePresence mode="wait">
              {step === 1 ? (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 16 }}
                  transition={{ duration: 0.25 }}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                >
                  {MODES.map((m) => {
                    const active = input.mode === m.key
                    return (
                      <button
                        key={m.key}
                        onClick={() => set('mode', m.key)}
                        className={`text-left rounded-xl border-2 p-5 transition-all ${
                          active
                            ? 'border-[#10B981] bg-emerald-50 shadow-md'
                            : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                        }`}
                      >
                        <span
                          className={`inline-flex items-center justify-center w-11 h-11 rounded-lg ${
                            active ? 'bg-[#10B981] text-white' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          <m.icon className="w-6 h-6" />
                        </span>
                        <div className="mt-3 font-bold text-[#0F172A]">{MODE_LABELS[m.key]}</div>
                        <div className="mt-1 text-sm text-slate-500">{m.sub}</div>
                      </button>
                    )
                  })}
                </motion.div>
              ) : (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.25 }}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                >
                  <div>
                    <label className={labelCls}>Origin (City / Zip)</label>
                    <input
                      className={fieldCls}
                      value={input.origin}
                      onChange={(e) => set('origin', e.target.value)}
                      placeholder="Chicago, IL / 60601"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Destination (City / Zip)</label>
                    <input
                      className={fieldCls}
                      value={input.destination}
                      onChange={(e) => set('destination', e.target.value)}
                      placeholder="Dallas, TX / 75201"
                    />
                  </div>

                  <div>
                    <label className={labelCls}>Weight</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min={0}
                        className={fieldCls}
                        value={input.weight}
                        onChange={(e) => set('weight', Number(e.target.value))}
                      />
                      <select
                        className="rounded-lg border border-slate-300 bg-white px-3 text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981]"
                        value={input.weightUnit}
                        onChange={(e) => set('weightUnit', e.target.value as CalcInput['weightUnit'])}
                      >
                        <option value="lbs">lbs</option>
                        <option value="kg">kg</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Cargo Type</label>
                    <select
                      className={fieldCls}
                      value={input.cargoType}
                      onChange={(e) => set('cargoType', e.target.value as CargoType)}
                    >
                      <option value="Standard">Standard</option>
                      <option value="Hazmat">Hazmat</option>
                      <option value="Refrigerated">Refrigerated</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className={labelCls}>Total Volume / Dimensions (L x W x H)</label>
                    <div className="flex flex-wrap gap-2">
                      <input
                        type="number"
                        min={0}
                        className={fieldCls + ' flex-1 min-w-[80px]'}
                        value={input.length}
                        onChange={(e) => set('length', Number(e.target.value))}
                        placeholder="L"
                      />
                      <input
                        type="number"
                        min={0}
                        className={fieldCls + ' flex-1 min-w-[80px]'}
                        value={input.width}
                        onChange={(e) => set('width', Number(e.target.value))}
                        placeholder="W"
                      />
                      <input
                        type="number"
                        min={0}
                        className={fieldCls + ' flex-1 min-w-[80px]'}
                        value={input.height}
                        onChange={(e) => set('height', Number(e.target.value))}
                        placeholder="H"
                      />
                      <select
                        className="rounded-lg border border-slate-300 bg-white px-3 text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981]"
                        value={input.dimUnit}
                        onChange={(e) => set('dimUnit', e.target.value as CalcInput['dimUnit'])}
                      >
                        <option value="in">in</option>
                        <option value="cm">cm</option>
                      </select>
                    </div>
                  </div>

                  {input.mode === 'OCEAN' ? (
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Ocean Service</label>
                      <div className="flex gap-2">
                        {(['FCL', 'LCL'] as const).map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => set('oceanContainer', c)}
                            className={`flex-1 rounded-lg border-2 py-2.5 font-semibold transition ${
                              input.oceanContainer === c
                                ? 'border-[#10B981] bg-emerald-50 text-[#0F172A]'
                                : 'border-slate-200 text-slate-600 hover:border-slate-300'
                            }`}
                          >
                            {c === 'FCL' ? 'Full Container (FCL)' : 'Less than Container (LCL)'}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation controls */}
            <div className="mt-8 flex items-center justify-between">
              {step === 2 ? (
                <button
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-slate-600 font-medium hover:bg-slate-100 transition"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              ) : (
                <span className="text-sm text-slate-400">Selected: {MODE_LABELS[input.mode]}</span>
              )}

              {step === 1 ? (
                <button
                  onClick={() => setStep(2)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#1E293B] hover:bg-[#0F172A] text-white font-semibold transition"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => setCalculated(true)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#10B981] hover:bg-[#0ea472] text-white font-semibold shadow-lg shadow-emerald-900/20 transition"
                >
                  Calculate Estimate
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Estimate result + lead gateway */}
        <AnimatePresence>
          {calculated ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6"
              id="leads"
            >
              <EstimateCard result={result} mode={input.mode} />
              <LeadForm input={input} estimatedRate={result.total} />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </section>
  )
}

function EstimateCard({ result, mode }: { result: ReturnType<typeof calculateFreight>; mode: TransportMode }) {
  const Icon = MODE_ICON[mode]
  const max = Math.max(...result.lines.map((l) => l.amount), 1)

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xl">
      <div className="flex items-center gap-2 text-slate-500">
        <Icon className="w-5 h-5 text-[#1E293B]" />
        <span className="text-sm font-medium">{MODE_LABELS[mode]} Estimate</span>
      </div>
      <div className="mt-2 text-4xl font-extrabold text-[#0F172A]">{formatUSD(result.total)}</div>
      <p className="mt-1 text-sm text-slate-500">{result.note}</p>

      <div className="mt-6 space-y-3">
        {result.lines.map((line) => (
          <div key={line.label}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">{line.label}</span>
              <span className="font-semibold text-[#0F172A]">{formatUSD(line.amount)}</span>
            </div>
            <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(line.amount / max) * 100}%` }}
                transition={{ duration: 0.5 }}
                className="h-full rounded-full bg-[#10B981]"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-400">
        Estimates are indicative baseline models. Firm carrier rates may vary by lane, fuel, and accessorials.
      </div>
    </div>
  )
}

export default Calculator
